import React, {useEffect, useState} from 'react';
import { isMediablockConnected } from '../../utils/mediablock';

const STATUS_CONFIG = {
    daemon: {
        online: { icon: 'fa-circle-check', color: 'text-success', label: 'Online' },
        offline: { icon: 'fa-circle-xmark', color: 'text-danger', label: 'Offline' },
        'not-installed': { icon: 'fa-circle-xmark', color: 'text-danger', label: 'Not installed' },
        default: { icon: 'fa-circle-question', color: 'text-secondary', label: 'Unknown' },
    },
    mediablockStatus: {
        Connected: { icon: 'fa-toggle-on', color: 'text-success', label: 'Connected' },
        Normal: { icon: 'fa-toggle-on', color: 'text-success', label: 'Connected' },
        default: { icon: 'fa-toggle-off', color: 'text-danger', label: 'Disconnected' },
    },
    mediablockState: {
        Enabled: { icon: 'fa-circle-check', color: 'text-success', label: 'Enabled' },
        Normal: { icon: 'fa-circle-check', color: 'text-success', label: 'Normal' },
        '': { icon: 'fa-circle-question', color: 'text-secondary', label: 'Unknown' },
        default: { icon: 'fa-circle-xmark', color: 'text-danger' },
    },
    tms: {
        running: { icon: 'fa-circle-check', color: 'text-success', label: 'Running' },
        paused: { icon: 'fa-circle-pause', color: 'text-warning', label: 'Paused' },
        'shut off': { icon: 'fa-circle-stop', color: 'text-danger', label: 'Shut off' },
        offline: { icon: 'fa-circle-xmark', color: 'text-danger', label: 'Offline' },
        default: { icon: 'fa-circle-question', color: 'text-secondary', label: 'Unknown' },
    },
};

const StatusIcon = ({ type, value, showLabel = false, className = '' }) => {
    const config = STATUS_CONFIG[type];
    if (!config) return null;

    const status = config[value] || config.default;
    const label = status.label || value || 'Unknown';

    return (
        <span className={className}>
            <span className={`fas ${status.icon} ${status.color} me-2`} />
            {showLabel && <span>{label}</span>}
        </span>
    );
};

// The on-site cronjob refreshes the mediablock, daemon and TMS values every 15
// minutes and stamps `lastSynced`. A stamp older than that means the statuses on
// screen are whatever the last successful run reported, not what the site looks
// like now — so they get flagged rather than read as live.
export const SYNC_INTERVAL_MS = 15 * 60 * 1000;

const plural = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

// "12 minutes", "2 hours and 5 minutes", "3 days and 4 hours" — hours alone stop
// being readable once a site has been silent for a couple of days. `compact`
// keeps the leading unit only ("3 days"), for the places that show the age in a
// chip and put the precise figure in a tooltip.
export const formatSyncAge = (ms, { compact = false } = {}) => {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return plural(minutes, 'minute');

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        const restMinutes = minutes % 60;
        return restMinutes && !compact
            ? `${plural(hours, 'hour')} and ${plural(restMinutes, 'minute')}`
            : plural(hours, 'hour');
    }

    const days = Math.floor(hours / 24);
    const restHours = hours % 24;
    return restHours && !compact
        ? `${plural(days, 'day')} and ${plural(restHours, 'hour')}`
        : plural(days, 'day');
};

const StatusRow = ({ label, value, ok }) => (
    <div className="d-flex justify-content-between gap-3">
        <span className="text-white-50">{label}</span>
        <span className={ok === undefined ? '' : ok ? 'text-success' : 'text-danger'}>{value || 'Unknown'}</span>
    </div>
);

const GroupTitle = ({ icon, first, children }) => (
    <div className={`fw-bold border-bottom pb-1 mb-2 ${first ? '' : 'mt-3'}`}>
        <span className={`fas ${icon} me-2`} />
        {children}
    </div>
);

// The values behind the three status icons, for tooltips and panels that show
// what "on" and "off" actually stand for.
export const SystemStatusDetails = ({ organization }) => {
    const mediablock = organization?.mediablock || {};
    const bacServer = organization?.bacServer || {};
    const tms = organization?.tmsSettings?.status;

    return (
        <div className="text-start p-1">
            <GroupTitle icon="fa-cube" first>Mediablock</GroupTitle>
            <StatusRow
                label="Status"
                value={mediablock.status}
                ok={isMediablockConnected(mediablock)}
            />
            <StatusRow
                label="State"
                value={mediablock.state}
                ok={['Enabled', 'Normal'].includes(mediablock.state)}
            />
            <StatusRow label="Lamp" value={mediablock.lamp} />
            <StatusRow label="Dowser" value={mediablock.dowser} />

            <GroupTitle icon="fa-server">BAC Server</GroupTitle>
            <StatusRow
                label="Alfred"
                value={bacServer.gofilexDaemonsAlfred}
                ok={bacServer.gofilexDaemonsAlfred === 'online'}
            />
            <StatusRow
                label="Frank"
                value={bacServer.gofilexDaemonsFrank}
                ok={bacServer.gofilexDaemonsFrank === 'online'}
            />

            <GroupTitle icon="fa-gears">TMS</GroupTitle>
            <StatusRow label="VM" value={tms} ok={tms === 'running'} />
        </div>
    );
};

// Warning shown next to a status that the cronjob hasn't refreshed in time.
// Renders nothing while the sync is current, so panels can drop it in unconditionally.
export const StaleSyncWarning = ({ lastSynced, className = '' }) => {
    // These panels stay open on a wall display; without a tick the age would be
    // frozen at whatever it was when the organization last loaded.
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(timer);
    }, []);

    const syncedAt = lastSynced ? new Date(lastSynced).getTime() : NaN;

    if (isNaN(syncedAt)) {
        return (
            <span className={`text-warning ${className}`}>
                <span className="fas fa-triangle-exclamation me-2" />
                Never synced
            </span>
        );
    }

    const age = now - syncedAt;
    if (age <= SYNC_INTERVAL_MS) return null;

    return (
        <span className={`text-warning ${className}`} title={`Last synced: ${new Date(syncedAt).toLocaleString()}`}>
            <span className="fas fa-triangle-exclamation me-2" />
            Last synced {formatSyncAge(age)} ago
        </span>
    );
};

export const StatusLegend = () => {
    const groups = [
        {
            title: 'Mediablock',
            icon: 'fa-cube',
            items: [
                { type: 'mediablockStatus', value: 'Connected', description: 'Connected' },
                { type: 'mediablockStatus', value: '', description: 'Disconnected' },
                { type: 'mediablockState', value: 'Enabled', description: 'Enabled' },
                { type: 'mediablockState', value: '', description: 'State unknown' },
            ]
        },
        {
            title: 'BAC Server (Alfred/Frank)',
            icon: 'fa-server',
            items: [
                { type: 'daemon', value: 'online', description: 'Running' },
                { type: 'daemon', value: 'offline', description: 'Not responding' },
                { type: 'daemon', value: 'not-installed', description: 'Not installed' },
                { type: 'daemon', value: '', description: 'Unknown' },
            ]
        },
        {
            title: 'TMS',
            icon: 'fa-gears',
            items: [
                { type: 'tms', value: 'running', description: 'VM running' },
                { type: 'tms', value: 'paused', description: 'VM paused' },
                { type: 'tms', value: 'shut off', description: 'VM stopped' },
                { type: 'tms', value: 'offline', description: 'No VM found' },
            ]
        },
    ];

    return (
        <div className="status-legend p-3 bg-dark rounded mt-4 mb-5">
            <h6 className="text-primary text-uppercase mb-3">Status Legend</h6>
            <div className="row">
                {groups.map((group, groupIndex) => (
                    <div key={groupIndex} className="col-12 col-lg-4 mb-3">
                        <div className="text-primary mb-2">
                            <span className={`fas ${group.icon} me-2`} />
                            {group.title}
                        </div>
                        {group.items.map((item, index) => (
                            <div key={index} className="mb-1 ps-3">
                                <StatusIcon type={item.type} value={item.value} showLabel />
                                <small className="text-secondary ms-1">- {item.description}</small>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const getSystemStatus = (organization) => ({
    mediablock: isMediablockConnected(organization?.mediablock) &&
                ['Enabled', 'Normal'].includes(organization?.mediablock?.state),
    bacServer: organization?.bacServer?.gofilexDaemonsAlfred === 'online' &&
               organization?.bacServer?.gofilexDaemonsFrank === 'online',
    tms: organization?.tmsSettings?.status === 'running',
});

export default StatusIcon;
